/**
 * Where the tours live, shown by motion (TOADO-981): when a tour or its offer
 * goes away, a copy of it shrinks into the Get started button at the top of
 * the page, so the person learns the tours can be found there again.
 *
 * The copy is drawn over the page and taken away when it lands; the real
 * box has already gone, so nothing waits on the animation and nothing can
 * be pressed in the copy. Under reduced motion it fades where it is. With no
 * Get started button on the page, or no animation support, nothing is drawn.
 */

/** The attribute the Get started button carries, for the copy to aim at. */
export const DOCK_ATTRIBUTE = 'data-tour-dock';

/** How long the copy takes to reach the button, and to fade under reduced motion. */
export const SHRINK_MS = 520;
const FADE_MS = 200;

function reducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Whether the button is there to aim at: in the page and drawn. */
export function dockOnPage(): HTMLElement | null {
  const dock = document.querySelector<HTMLElement>(`[${DOCK_ATTRIBUTE}]`);
  if (!dock) return null;
  const box = dock.getBoundingClientRect();
  return box.width > 0 && box.height > 0 ? dock : null;
}

/**
 * Shrinks a copy of `from` into the Get started button. Call it before
 * `from` leaves the page, since it measures it; returns at once.
 */
export function shrinkIntoDock(from: Element | null): void {
  if (!from || typeof HTMLElement === 'undefined') return;
  const dock = dockOnPage();
  const start = from.getBoundingClientRect();
  if (!dock || start.width === 0 || start.height === 0) return;
  const end = dock.getBoundingClientRect();
  const ghost = from.cloneNode(true) as HTMLElement;
  if (typeof ghost.animate !== 'function') return;

  // A picture of the box, nothing more: no ids to clash with, nothing a
  // screen reader or a click can reach, and none of the attributes the
  // tours and the tests find the real box by.
  for (const node of [ghost, ...ghost.querySelectorAll('*')]) {
    node.removeAttribute('id');
    for (const name of [...node.getAttributeNames()]) {
      if (name.startsWith('data-tour') || name.startsWith('data-testid') || name.startsWith('aria-')) node.removeAttribute(name);
    }
  }
  ghost.removeAttribute('role');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  ghost.setAttribute('data-tour-ghost', '');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: `${start.top}px`,
    left: `${start.left}px`,
    right: 'auto',
    bottom: 'auto',
    width: `${start.width}px`,
    height: `${start.height}px`,
    margin: '0',
    maxWidth: 'none',
    pointerEvents: 'none',
    // The source's own entry animation, which the copy's classes carry, does not run again under the shrink.
    animation: 'none',
    transition: 'none',
    transformOrigin: 'center center',
    zIndex: 'var(--z-toast, 1000)',
  });
  document.body.appendChild(ghost);

  const reduce = reducedMotion();
  const dx = end.left + end.width / 2 - (start.left + start.width / 2);
  const dy = end.top + end.height / 2 - (start.top + start.height / 2);
  const scale = Math.max(0.02, Math.min(end.width / start.width, end.height / start.height));
  const frames: Keyframe[] = reduce
    ? [{ opacity: 1 }, { opacity: 0 }]
    : [
      { transform: 'translate(0, 0) scale(1)', opacity: 1, borderRadius: '12px' },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.55}px) scale(${0.55 + scale * 0.45})`, opacity: 0.9, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.25, borderRadius: '999px' },
    ];
  const duration = reduce ? FADE_MS : SHRINK_MS;
  let landed = false;
  const land = () => {
    if (landed) return;
    landed = true;
    ghost.remove();
    if (!reduce && dock.isConnected && typeof dock.animate === 'function') {
      // A small bump as it arrives, so the eye ends on the button.
      dock.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.22)' }, { transform: 'scale(1)' }],
        { duration: 320, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' },
      );
    }
  };
  const animation = ghost.animate(frames, { duration, easing: reduce ? 'linear' : 'cubic-bezier(0.55, 0, 0.75, 0.2)', fill: 'forwards' });
  animation.onfinish = land;
  animation.oncancel = land;
  // Never left on the page, whatever becomes of the animation.
  window.setTimeout(land, duration + 400);
}
